/**
 * UserDetailBody.jsx — Unified Player & Member Dossier Body.
 *
 * Shared by both UserDetail (window widget) and UserDetailModal (popup).
 * Organizes member & PC-NA player data into:
 *   1. Cross-board summary header + 4-card KPI strip (Sales, Lifetime/TTC, Raffle, Sync/Activity)
 *   2. Tabbed, filterable, sortable ThemedTable views + collapsible sections:
 *      - 💰 Sales & TTC
 *      - 🎟️ Raffle & Dues
 *      - 🛍️ Purchases & Intel
 *      - 🔧 Technical, Sync & Admin Controls
 */
import { Show, For, Switch, Match, createSignal, createEffect } from 'solid-js';
import { gold, goldPerEach, qty, relTime, tsMs, esoIconUrl, ESO_ICON_FALLBACK, qualityColor } from '../lib/format.js';
import { pickItem, pickGuild, pickUser } from '../lib/picks.js';
import ThemedTable, { GoldCell, GuildCell, UserCell, RelTimeCell } from '../components/ThemedTable/ThemedTable.jsx';
import wm from '../lib/wmStore.js';
import toast from '../lib/toastStore.js';
import Tooltip from '../components/MouseTooltip.jsx';
import Avatar from '../components/Avatar.jsx';
import { EMOJI } from '../lib/emoji.js';
import './UserDetailBody.css';

const SPY_TIP =
  'Spy as this user (admin)\n\n' +
  'Reloads the dashboard with ?as=<their id>, showing their data and ' +
  'permissions as if you were signed in as them. Use "Stop spying" ' +
  'in the SpyBar to return.';

const ROLE_LABELS = {
  owner: 'Owner',
  family: 'Family',
  staff: 'Redfur Staff',
  member: 'Redfur Member',
};

function spyAs(id) {
  if (!id) return;
  const url = new URL(window.location.href);
  url.searchParams.set('as', String(id));
  window.location.href = url.toString();
}

function formatDateShort(ts) {
  const ms = tsMs(ts);
  if (!ms) return '—';
  try {
    return new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
}

async function patchAdminProfile(id, body) {
  const response = await fetch(`/api/v2/profile-management/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
  return data.profile;
}

export default function UserDetailBody(props) {
  const tag  = () => props.tag;
  const data = () => props.data;
  const user = () => data()?.user;
  const [activeTab, setActiveTab] = createSignal(props.initialTab || 'sales');
  const [savingProfile, setSavingProfile] = createSignal(false);

  createEffect(() => {
    if (props.initialTab) setActiveTab(props.initialTab);
  });

  const weekOffset = () => Number(props.weekOffset ?? user()?.selectedWeek?.weekOffset ?? 0) || 0;
  const weekLabel = () => weekOffset() === 0 ? 'This week' : `${weekOffset()} week${weekOffset() === 1 ? '' : 's'} ago`;
  const isAdmin = () => !!wm.state.identity?.isAdmin;
  const isOwner = () => !!wm.state.identity?.isOwner;
  const alreadySpying = () =>
    !!wm.state.identity?.impersonating &&
    user()?.discordId &&
    String(wm.state.identity?.targetId || '') === String(user().discordId);

  const dossier = () => user()?.playerDossier || null;
  const tech = () => user()?.technical || {};
  const adminProf = () => user()?.adminProfile || null;
  const buyerIntel = () => user()?.ttcIntel?.buyerIntel || null;

  // ── Table Column Definitions ──────────────────────────────────────────
  const salesColumns = [
    {
      key: 'itemName',
      label: 'Item',
      type: 'string',
      canHide: false,
      render: (val, row) => (
        <span class="v2-user-detail__item-cell">
          <Show when={row.itemIcon}>
            <img
              class="v2-item-icon v2-item-icon--xs"
              src={esoIconUrl(row.itemIcon)}
              alt=""
              onError={(e) => { e.currentTarget.src = ESO_ICON_FALLBACK; }}
            />
          </Show>
          <span
            class="v2-pick v2-pick--item"
            style={row.quality ? { color: qualityColor(row.quality) } : undefined}
            onClick={(e) => { e.stopPropagation(); pickItem(row.itemName, { icon: row.itemIcon }); }}
          >
            {val || 'Unknown Item'}
          </span>
        </span>
      ),
    },
    {
      key: 'quant',
      label: 'Qty',
      type: 'number',
      align: 'right',
      width: '58px',
      render: (val) => qty(val || 1),
    },
    {
      key: 'price',
      label: 'Gold',
      type: 'currency',
      align: 'right',
      render: (val, row) => (
        <GoldCell
          value={val}
          perEach={row.quant > 1 ? goldPerEach(val, row.quant) : null}
        />
      ),
    },
    {
      key: 'guildName',
      label: 'Guild / Source',
      type: 'string',
      render: (val) => val ? <GuildCell name={val} /> : <span class="v2-dim">—</span>,
    },
    {
      key: 'buyer',
      label: 'Counterparty',
      type: 'string',
      defaultVisible: false,
      getValue: (row) => row.buyer || row.seller || '',
      render: (val) => val ? <UserCell name={val} /> : <span class="v2-dim">—</span>,
    },
    {
      key: 'timestamp',
      label: 'When',
      type: 'time',
      align: 'right',
      width: '92px',
      render: (val) => <RelTimeCell ts={val} />,
    },
  ];

  const raffleColumns = [
    {
      key: 'weekStart',
      label: 'Week Starting',
      type: 'time',
      canHide: false,
      render: (val) => <span>{formatDateShort(val)}</span>,
    },
    {
      key: 'guildKey',
      label: 'Guild',
      type: 'string',
      render: (val) => (
        <span class="v2-badge">
          {val === 'post' ? 'Redfur Trading Post' : val === 'dealers' ? 'Redfur Dealers' : val}
        </span>
      ),
    },
    {
      key: 'tickets',
      label: 'Tickets',
      type: 'number',
      align: 'right',
      render: (val) => <b>{qty(val || 0)}</b>,
    },
    {
      key: 'entries',
      label: 'Deposits',
      type: 'number',
      align: 'right',
      render: (val) => qty(val || 0),
    },
    {
      key: 'gold',
      label: 'Gold Deposited',
      type: 'currency',
      align: 'right',
      render: (val) => <GoldCell value={val} />,
    },
  ];

  const purchaseColumns = [
    {
      key: 'itemName',
      label: 'Item Bought',
      type: 'string',
      canHide: false,
      render: (val, row) => (
        <span class="v2-user-detail__item-cell">
          <Show when={row.icon || row.itemIcon}>
            <img
              class="v2-item-icon v2-item-icon--xs"
              src={esoIconUrl(row.icon || row.itemIcon)}
              alt=""
              onError={(e) => { e.currentTarget.src = ESO_ICON_FALLBACK; }}
            />
          </Show>
          <span
            class="v2-pick v2-pick--item"
            onClick={(e) => { e.stopPropagation(); pickItem(val, { icon: row.icon || row.itemIcon }); }}
          >
            {val || 'Unknown'}
          </span>
        </span>
      ),
    },
    {
      key: 'quant',
      label: 'Qty',
      type: 'number',
      align: 'right',
      width: '54px',
      getValue: (row) => row.quant || row.amount || 1,
      render: (val) => qty(val || 1),
    },
    {
      key: 'price',
      label: 'Paid',
      type: 'currency',
      align: 'right',
      getValue: (row) => row.price || row.totalPrice || 0,
      render: (val, row) => <GoldCell value={val} perEach={(row.quant || row.amount) > 1 ? goldPerEach(val, row.quant || row.amount) : null} />,
    },
    {
      key: 'esoHubAvgSale',
      label: 'ESO-Hub Avg',
      type: 'currency',
      align: 'right',
      getValue: (row) => (row.esoHubAvgSale || 0) * (row.quant || row.amount || 1),
      render: (val) => val > 0 ? <GoldCell value={val} /> : <span class="v2-dim">—</span>,
    },
    {
      key: 'vsEsoHubRatio',
      label: 'Ratio',
      type: 'number',
      align: 'right',
      width: '68px',
      render: (val) => val != null ? (
        <span class={val < 0.75 ? 'v2-text-success' : val > 1.25 ? 'v2-text-warn' : ''}>
          {val}x
        </span>
      ) : <span class="v2-dim">—</span>,
    },
    {
      key: 'seller',
      label: 'Seller',
      type: 'string',
      render: (val) => val ? <UserCell name={val} /> : <span class="v2-dim">—</span>,
    },
    {
      key: 'guildName',
      label: 'Kiosk Guild',
      type: 'string',
      render: (val) => val ? <GuildCell name={val} /> : <span class="v2-dim">—</span>,
    },
    {
      key: 'timestamp',
      label: 'When',
      type: 'time',
      align: 'right',
      width: '88px',
      render: (val) => <RelTimeCell ts={val} />,
    },
  ];

  const syncSessionColumns = [
    {
      key: 'id',
      label: 'Session',
      type: 'number',
      width: '72px',
      canHide: false,
      render: (val) => <span class="v2-tt-mono">#{val}</span>,
    },
    {
      key: 'sync_type',
      label: 'Type / Source',
      type: 'string',
      render: (val, row) => (
        <span>
          <b>{String(val || 'MM').toUpperCase()}</b>
          <Show when={row.source}>
            <span class="v2-dim"> · {row.source}</span>
          </Show>
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      type: 'string',
      width: '86px',
      render: (val) => (
        <span class={`v2-badge ${val === 'ok' || val === 'completed' ? 'v2-badge--ok' : ''}`}>
          {val || 'ok'}
        </span>
      ),
    },
    {
      key: 'sales_new',
      label: 'New / Total Sales',
      type: 'number',
      align: 'right',
      render: (val, row) => (
        <span class="v2-tt-mono">
          +{qty(val || 0)} <span class="v2-dim">/ {qty(row.sales_total || 0)}</span>
        </span>
      ),
    },
    {
      key: 'started_at',
      label: 'Synced',
      type: 'time',
      align: 'right',
      width: '96px',
      render: (val) => <RelTimeCell ts={val} />,
    },
  ];

  async function handleProfileSave(e) {
    e.preventDefault();
    const prof = adminProf();
    if (!prof || prof.unlinked) return;
    const form = e.currentTarget;
    setSavingProfile(true);
    try {
      await patchAdminProfile(prof.id, {
        role: form.role.value,
        familyMember: form.familyMember.checked,
        relayContributor: form.relayContributor.checked,
        notes: form.notes.value,
      });
      toast.success('Profile updated', { detail: user()?.tag });
      if (typeof props.onRefetch === 'function') await props.onRefetch();
    } catch (err) {
      toast.error('Profile update failed', { detail: err.message });
    } finally {
      setSavingProfile(false);
    }
  }

  const purchaseRows = () =>
    dossier()?.snipeLedger?.length
      ? dossier().snipeLedger
      : (buyerIntel()?.recentPurchases || []);

  return (
    <Show when={!props.loading} fallback={<div class="v2-widget-skel">Loading dossier…</div>}>
      <Show when={!props.error} fallback={<div class="v2-error">{props.error}</div>}>
        <Show when={user()} fallback={
          <div class="v2-empty">
            {data()?.restricted
              ? `🔒 ${data().reason || 'Staff authorization required to inspect other players.'}`
              : `No data for ${tag() || 'this user'}.`}
          </div>
        }>
          <div class="v2-user-detail">
            {/* ── 1. Cross-Board Identity & Summary Header ─────────────────── */}
            <div class="v2-user-detail__banner">
              <div class="v2-user-detail__head">
                <Show when={user().avatarUrl} fallback={<span class="v2-user-detail__fallback-avatar">🐾</span>}>
                  <Avatar url={user().avatarUrl} name={user().tag} size={42} />
                </Show>
                <div class="v2-user-detail__head-text">
                  <div class="v2-user-detail__tagline">
                    <span>{user().tag}</span>
                    <Show when={user().verified}>
                      <span class="v2-user-detail__verified">✓ verified</span>
                    </Show>
                    <Show when={dossier()?.affiliation}>
                      <span class="v2-user-detail__verified" title={dossier().affiliation.detail}>
                        {dossier().affiliation.label}
                      </span>
                    </Show>
                    <Show when={dossier()?.archetype}>
                      <span class="v2-user-detail__verified" title={dossier().archetype.description}>
                        {dossier().archetype.emoji} {dossier().archetype.label}
                      </span>
                    </Show>
                    <Show when={adminProf()?.role}>
                      <span class="v2-user-detail__privacy">
                        {ROLE_LABELS[adminProf().role] || adminProf().role}
                      </span>
                    </Show>
                    <Show when={isAdmin() && user().privacy}>
                      <span class="v2-user-detail__privacy">👁️ privacy see-through</span>
                    </Show>
                  </div>

                  <div class="v2-user-detail__meta-line">
                    <Show when={user().discordId}>
                      <span>
                        💬 Discord:{' '}
                        <a
                          class="v2-md-link"
                          href={`https://discord.com/users/${user().discordId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {user().discordId}
                        </a>
                      </span>
                    </Show>
                    <Show when={tech().firstSeenTs}>
                      <span>📅 First seen: <b>{formatDateShort(tech().firstSeenTs)}</b></span>
                    </Show>
                    <Show when={tech().lastSeenTs}>
                      <span>⚡ Last active: <b>{relTime(tsMs(tech().lastSeenTs))}</b></span>
                    </Show>
                    <Show when={isAdmin() && user().discordId && !alreadySpying()}>
                      <Tooltip text={SPY_TIP}>
                        <button
                          type="button"
                          class="v2-btn-sm v2-user-detail__spy"
                          onClick={() => spyAs(user().discordId)}
                          aria-label={`Spy as ${user().tag}`}
                        >
                          {EMOJI.spy} Spy
                        </button>
                      </Tooltip>
                    </Show>
                    <Show when={isAdmin() && alreadySpying()}>
                      <span class="v2-dim">{EMOJI.spying} spying</span>
                    </Show>
                  </div>

                  <Show when={user().guildMemberships?.length}>
                    <div class="v2-user-detail__guilds-line">
                      🏪 Guilds (90d):{' '}
                      <For each={user().guildMemberships}>{(g, i) => (
                        <>
                          <Show when={i() > 0}>{', '}</Show>
                          <span class="v2-pick v2-pick--guild" onClick={() => pickGuild(g.guildName)}>
                            {g.guildName}
                          </span>
                          <span class="v2-dim" style={{ 'font-size': '10px' }}>
                            {' '}({qty(g.saleCount)})
                          </span>
                        </>
                      )}</For>
                    </div>
                  </Show>
                </div>
              </div>

              <Show when={props.onWeekOffset}>
                <div class="v2-user-detail__weekbar" aria-label="Trade week filter">
                  <For each={[0, 1, 2, 3, 4]}>{(offset) => (
                    <button
                      type="button"
                      class={`v2-btn-sm ${weekOffset() === offset ? 'is-active' : ''}`}
                      onClick={() => props.onWeekOffset(offset)}
                    >
                      {offset === 0 ? 'This week' : `${offset}w ago`}
                    </button>
                  )}</For>
                </div>
              </Show>

              <Show when={user().hidden?.saleHistory}>
                <div class="v2-user-detail__private-note">
                  This profile hides public sale history, totals, raffle activity, and item history.
                </div>
              </Show>

              {/* ── 4-Card Cross-Board KPI Strip ── */}
              <Show when={!user().hidden?.saleHistory}>
                <div class="v2-kpi-strip v2-user-detail__kpis">
                  <div class="v2-kpi-card">
                    <span class="v2-kpi-card__label">{weekLabel()} Sales</span>
                    <strong class="v2-kpi-card__value v2-kpi-card__value--gold">{gold(user().totalGold || 0)}</strong>
                    <span class="v2-kpi-card__sub">
                      {qty(user().totalSales || 0)} sales · {qty(user().totalItems || 0)} items
                    </span>
                  </div>

                  <div class="v2-kpi-card">
                    <span class="v2-kpi-card__label">Lifetime & TTC Sales</span>
                    <strong class="v2-kpi-card__value">
                      {gold((user().lifetime?.totalGold || 0) || (user().ttcIntel?.totalAttributedGold || 0))}
                    </strong>
                    <span class="v2-kpi-card__sub">
                      {qty(user().lifetime?.totalSales || 0)} guild · {qty(user().ttcIntel?.totalAttributedCount || 0)} TTC
                    </span>
                  </div>

                  <div class="v2-kpi-card">
                    <span class="v2-kpi-card__label">Raffle & Standing</span>
                    <strong class="v2-kpi-card__value">
                      {qty(user().raffle?.totalTickets || 0)} tickets
                    </strong>
                    <span class="v2-kpi-card__sub">
                      {gold(user().raffle?.totalGold || 0)} · Score {user().performance?.score ?? 0}/100
                    </span>
                  </div>

                  <div class="v2-kpi-card">
                    <span class="v2-kpi-card__label">Purchases & Sync</span>
                    <strong class="v2-kpi-card__value">
                      {buyerIntel()?.totalSpentGold > 0
                        ? gold(buyerIntel().totalSpentGold)
                        : `${qty(tech().syncStats?.points || 0)} sync pts`}
                    </strong>
                    <span class="v2-kpi-card__sub">
                      {qty(buyerIntel()?.totalPurchases || 0)} buys · {qty(tech().syncStats?.mmSyncs || 0)} MM / {qty(tech().syncStats?.ttcSyncs || 0)} TTC
                    </span>
                  </div>
                </div>
              </Show>
            </div>

            {/* ── 2. Segmented Tab Bar ─────────────────────────────────────── */}
            <div class="v2-tabs v2-user-detail__tabs" role="tablist" aria-label="User dossier sections">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab() === 'sales'}
                class={`v2-tab ${activeTab() === 'sales' ? 'is-active' : ''}`}
                onClick={() => setActiveTab('sales')}
              >
                💰 Sales & TTC ({qty(user().recent?.length || 0)})
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab() === 'raffle'}
                class={`v2-tab ${activeTab() === 'raffle' ? 'is-active' : ''}`}
                onClick={() => setActiveTab('raffle')}
              >
                🎟️ Raffle & Dues ({qty(user().raffle?.totalTickets || 0)})
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab() === 'purchases'}
                class={`v2-tab ${activeTab() === 'purchases' ? 'is-active' : ''}`}
                onClick={() => setActiveTab('purchases')}
              >
                🛍️ Purchases & Intel ({qty(buyerIntel()?.totalPurchases || 0)})
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab() === 'tech'}
                class={`v2-tab ${activeTab() === 'tech' ? 'is-active' : ''}`}
                onClick={() => setActiveTab('tech')}
              >
                🔧 Technical & Sync
                <Show when={isAdmin()}><span> · 🛡️</span></Show>
              </button>
            </div>

            {/* ── 3. Active Tab Content ────────────────────────────────────── */}
            <div class="v2-user-detail__tab-panel">
              <Switch>
                {/* TAB 1: SALES & TTC */}
                <Match when={activeTab() === 'sales'}>
                  <div class="v2-user-detail__pane">
                    <ThemedTable
                      storageKey="ud_sales"
                      compact={true}
                      columns={salesColumns}
                      rows={user().recent || []}
                      defaultSortKey="timestamp"
                      defaultSortDir="desc"
                      searchPlaceholder="Filter sales by item, guild, or buyer…"
                      emptyText={`No sales recorded for ${user().tag} in this period.`}
                    />

                    <Show when={user().topItems?.length}>
                      <details class="v2-collapsible" open>
                        <summary class="v2-collapsible__summary">
                          📦 Top Items ({qty(user().topItems.length)})
                        </summary>
                        <div class="v2-collapsible__body">
                          <ol class="v2-rank-list">
                            <For each={user().topItems}>{(it, i) => (
                              <li class="v2-rank-row">
                                <span class="v2-rank-row__medal">{i() + 1}</span>
                                <Show when={it.itemIcon}>
                                  <img
                                    class="v2-item-icon v2-item-icon--xs"
                                    src={esoIconUrl(it.itemIcon)}
                                    alt=""
                                    onError={(e) => { e.currentTarget.src = ESO_ICON_FALLBACK; }}
                                  />
                                </Show>
                                <span style={{ 'min-width': 0 }}>
                                  <span
                                    class="v2-rank-row__name v2-pick v2-pick--item"
                                    style={it.quality ? { color: qualityColor(it.quality) } : undefined}
                                    onClick={() => pickItem(it.itemName, { icon: it.itemIcon })}
                                  >
                                    {it.itemName}
                                  </span>
                                  <span class="v2-rank-row__sub">
                                    {qty(it.totalSales)} {user().recentScope === 'buyer_activity' ? 'buys' : 'sales'} · {qty(it.totalItems)} items
                                    <Show when={it.esoHubAvgSale > 0}> · ESO-Hub avg {gold(it.esoHubAvgSale)}</Show>
                                  </span>
                                </span>
                                <span class="v2-rank-row__value">{gold(it.totalGold)}</span>
                              </li>
                            )}</For>
                          </ol>
                        </div>
                      </details>
                    </Show>

                    <Show when={user().ttcIntel?.resolvedListings?.length || dossier()?.relistMatches?.length}>
                      <details class="v2-collapsible">
                        <summary class="v2-collapsible__summary">
                          🧭 TTC Kiosk Disappearances & External Relists ({qty((user().ttcIntel?.resolvedListings?.length || 0) + (dossier()?.relistMatches?.length || 0))})
                        </summary>
                        <div class="v2-collapsible__body">
                          <Show when={dossier()?.relistMatches?.length}>
                            <div class="v2-section-h">🦈 Detected Kiosk Flip / Relist Matches</div>
                            <div class="v2-user-detail__ttc-list">
                              <For each={dossier().relistMatches}>{(m) => (
                                <div class="v2-user-detail__ttc-row">
                                  <span class="v2-ttc-badge v2-ttc-badge--sold_implied">
                                    +{m.markupPct}% Flip
                                  </span>
                                  <span class="v2-user-detail__ttc-main">
                                    <span class="v2-pick v2-pick--item" onClick={() => pickItem(m.itemName)}>
                                      {m.itemName}
                                    </span>
                                    <span class="v2-user-detail__ttc-note">
                                      Bought @ {gold(m.boughtUnitPrice)} ({m.boughtFromGuild}) → Relisted @ {gold(m.relistUnitPrice)} ({m.relistedInGuild})
                                    </span>
                                  </span>
                                  <span class="v2-user-detail__ttc-price">+{gold(m.potentialProfitGold)}</span>
                                </div>
                              )}</For>
                            </div>
                          </Show>

                          <Show when={user().ttcIntel?.resolvedListings?.length}>
                            <div class="v2-user-detail__ttc-list">
                              <For each={user().ttcIntel.resolvedListings}>{(row) => (
                                <div class="v2-user-detail__ttc-row">
                                  <span class={`v2-ttc-badge v2-ttc-badge--${(row.status || '').toLowerCase()}`}>
                                    {row.status === 'SOLD_CONFIRMED' ? '✓ Sold'
                                      : row.status === 'SOLD_IMPLIED' ? '⚡ Implied'
                                      : row.status === 'CANCELLED_RELIST' ? '↺ Undercut'
                                      : '⌛ Expired'}
                                  </span>
                                  <span class="v2-user-detail__ttc-main">
                                    <Show when={row.icon}>
                                      <img
                                        class="v2-item-icon v2-item-icon--xs"
                                        src={esoIconUrl(row.icon)}
                                        alt=""
                                        onError={(e) => { e.currentTarget.src = ESO_ICON_FALLBACK; }}
                                      />
                                    </Show>
                                    <span class="v2-pick v2-pick--item" onClick={() => pickItem(row.itemName, { icon: row.icon })}>
                                      {row.itemName}
                                    </span>
                                    <Show when={row.amount > 1}><em> ×{row.amount}</em></Show>
                                    <Show when={row.resolutionNote}>
                                      <span class="v2-user-detail__ttc-note">{row.resolutionNote}</span>
                                    </Show>
                                  </span>
                                  <span class="v2-user-detail__ttc-price">{gold(row.totalPrice)}</span>
                                </div>
                              )}</For>
                            </div>
                          </Show>
                        </div>
                      </details>
                    </Show>
                  </div>
                </Match>

                {/* TAB 2: RAFFLE & DUES */}
                <Match when={activeTab() === 'raffle'}>
                  <div class="v2-user-detail__pane">
                    <Show when={user().raffle?.currentByGuild?.length}>
                      <div class="v2-kpi-strip">
                        <For each={user().raffle.currentByGuild}>{(g) => (
                          <div class="v2-kpi-card">
                            <span class="v2-kpi-card__label">{g.guildLabel} (Current Week)</span>
                            <strong class="v2-kpi-card__value v2-kpi-card__value--gold">
                              {qty(g.tickets || 0)} tickets
                            </strong>
                            <span class="v2-kpi-card__sub">
                              {gold(g.gold || 0)} deposited
                              <Show when={g.rank}> · Rank #{g.rank} of {g.participants}</Show>
                            </span>
                          </div>
                        )}</For>
                      </div>
                    </Show>

                    <Show when={user().duesByGuild?.length}>
                      <details class="v2-collapsible" open>
                        <summary class="v2-collapsible__summary">
                          🛡️ Guild Tax & Dues Compliance ({qty(user().duesByGuild.length)} guilds)
                        </summary>
                        <div class="v2-collapsible__body">
                          <div class="v2-user-detail__context-grid">
                            <For each={user().duesByGuild}>{(d) => (
                              <div class="v2-user-detail__context-card">
                                <span class="v2-user-detail__context-k">{d.guildName} · {d.rankName || 'Member'}</span>
                                <strong>{gold(d.totalContribution || 0)} contributed</strong>
                                <span>
                                  Tax {gold(d.salesTax || 0)} + Bank {gold(d.directDeposits || 0)}
                                  <Show when={d.expectedMinimum > 0}>
                                    {' '}· Target {gold(d.expectedMinimum)} ({d.statusLabel || (d.meetsMinimum ? '✓ Met' : 'Below')})
                                  </Show>
                                </span>
                              </div>
                            )}</For>
                          </div>
                        </div>
                      </details>
                    </Show>

                    <ThemedTable
                      storageKey="ud_raffle"
                      compact={true}
                      columns={raffleColumns}
                      rows={user().raffle?.recent || []}
                      defaultSortKey="weekStart"
                      defaultSortDir="desc"
                      searchPlaceholder="Filter raffle history by guild…"
                      emptyText="No historical raffle entries recorded for this player."
                    />
                  </div>
                </Match>

                {/* TAB 3: PURCHASES & INTEL */}
                <Match when={activeTab() === 'purchases'}>
                  <div class="v2-user-detail__pane">
                    <Show when={dossier()?.guildsBoughtFrom?.length || buyerIntel()?.topBoughtItems?.length}>
                      <details class="v2-collapsible" open>
                        <summary class="v2-collapsible__summary">
                          🏆 Shopping Footprint & Top Bought Items
                        </summary>
                        <div class="v2-collapsible__body">
                          <Show when={dossier()?.guildsBoughtFrom?.length}>
                            <div class="v2-user-detail__guilds-line" style={{ 'margin-bottom': '8px' }}>
                              🏪 Kiosks Shopped:{' '}
                              <For each={dossier().guildsBoughtFrom}>{(g, i) => (
                                <>
                                  <Show when={i() > 0}>{', '}</Show>
                                  <span class="v2-pick v2-pick--guild" onClick={() => pickGuild(g.guildName)}>
                                    {g.guildName}
                                  </span>
                                  <span class="v2-dim" style={{ 'font-size': '10px' }}>
                                    {' '}({gold(g.totalGold)} · {qty(g.txCount)})
                                  </span>
                                </>
                              )}</For>
                            </div>
                          </Show>

                          <Show when={buyerIntel()?.topBoughtItems?.length}>
                            <ol class="v2-rank-list">
                              <For each={buyerIntel().topBoughtItems}>{(it, i) => (
                                <li class="v2-rank-row">
                                  <span class="v2-rank-row__medal">{i() + 1}</span>
                                  <Show when={it.icon}>
                                    <img
                                      class="v2-item-icon v2-item-icon--xs"
                                      src={esoIconUrl(it.icon)}
                                      alt=""
                                      onError={(e) => { e.currentTarget.src = ESO_ICON_FALLBACK; }}
                                    />
                                  </Show>
                                  <span style={{ 'min-width': 0 }}>
                                    <span class="v2-rank-row__name v2-pick v2-pick--item" onClick={() => pickItem(it.itemName, { icon: it.icon })}>
                                      {it.itemName}
                                    </span>
                                    <span class="v2-rank-row__sub">
                                      {qty(it.purchases)} buys · {qty(it.totalQuant)} units
                                      <Show when={it.esoHubAvgSale > 0}> · ESO-Hub avg {gold(it.esoHubAvgSale)}</Show>
                                    </span>
                                  </span>
                                  <span class="v2-rank-row__value">{gold(it.totalSpent)}</span>
                                </li>
                              )}</For>
                            </ol>
                          </Show>
                        </div>
                      </details>
                    </Show>

                    <ThemedTable
                      storageKey="ud_purchases"
                      compact={true}
                      columns={purchaseColumns}
                      rows={purchaseRows()}
                      defaultSortKey="timestamp"
                      defaultSortDir="desc"
                      searchPlaceholder="Filter purchases by item, seller, or guild…"
                      emptyText="No kiosk purchases recorded for this player."
                    />
                  </div>
                </Match>

                {/* TAB 4: TECHNICAL, SYNC & ADMIN GOVERNANCE */}
                <Match when={activeTab() === 'tech'}>
                  <div class="v2-user-detail__pane">
                    <div class="v2-user-detail__context-grid">
                      <div class="v2-user-detail__context-card">
                        <span class="v2-user-detail__context-k">ESO Account & ID</span>
                        <strong>{user().tag}</strong>
                        <span>UserData ID: {tech().esoUserId ?? '—'} · {user().verified ? 'Verified' : 'Unverified'}</span>
                      </div>
                      <div class="v2-user-detail__context-card">
                        <span class="v2-user-detail__context-k">Discord & Link</span>
                        <strong>{user().discordId || 'Not Linked'}</strong>
                        <span>
                          Source: {tech().linkSource || '—'}
                          <Show when={tech().linkedAt}> · Linked {formatDateShort(tech().linkedAt)}</Show>
                        </span>
                      </div>
                      <div class="v2-user-detail__context-card">
                        <span class="v2-user-detail__context-k">Activity Horizon</span>
                        <strong>First seen: {formatDateShort(tech().firstSeenTs)}</strong>
                        <span>Last seen: {tech().lastSeenTs ? `${relTime(tsMs(tech().lastSeenTs))} (${formatDateShort(tech().lastSeenTs)})` : '—'}</span>
                      </div>
                      <div class="v2-user-detail__context-card">
                        <span class="v2-user-detail__context-k">Relay & Sync Telemetry</span>
                        <strong>{qty(tech().syncStats?.points || 0)} Sync Points</strong>
                        <span>
                          {qty(tech().syncStats?.mmSyncs || 0)} MM syncs · {qty(tech().syncStats?.ttcSyncs || 0)} TTC syncs · {qty(tech().relayDevices?.length || 0)} devices
                        </span>
                      </div>
                    </div>

                    <Show when={tech().relayDevices?.length}>
                      <details class="v2-collapsible" open>
                        <summary class="v2-collapsible__summary">
                          📡 Linked Relay Devices ({qty(tech().relayDevices.length)})
                        </summary>
                        <div class="v2-collapsible__body">
                          <div class="v2-user-detail__context-grid">
                            <For each={tech().relayDevices}>{(dev) => (
                              <div class="v2-user-detail__context-card">
                                <span class="v2-user-detail__context-k">{dev.platform || 'Relay Client'} · v{dev.client_version || '1.0'}</span>
                                <strong>{dev.device_name || dev.device_id}</strong>
                                <span>
                                  {qty(dev.sync_count || 0)} syncs · Last seen {dev.last_seen_at ? relTime(tsMs(dev.last_seen_at)) : '—'}
                                </span>
                              </div>
                            )}</For>
                          </div>
                        </div>
                      </details>
                    </Show>

                    <ThemedTable
                      storageKey="ud_syncs"
                      compact={true}
                      columns={syncSessionColumns}
                      rows={tech().recentSyncSessions || []}
                      defaultSortKey="started_at"
                      defaultSortDir="desc"
                      searchPlaceholder="Filter sync sessions by type or source…"
                      emptyText="No sync sessions recorded for this account."
                      onRowClick={(row) => {
                        if (row?.id && typeof window !== 'undefined') {
                          window.dispatchEvent(new CustomEvent('v2-sync-session-pick', { detail: { sessionId: row.id } }));
                        }
                      }}
                    />

                    {/* Tagged-In Admin Governance Controls */}
                    <Show when={isAdmin() && adminProf()}>
                      <details class="v2-collapsible" open>
                        <summary class="v2-collapsible__summary">
                          🛡️ Admin Profile Governance & Permissions ({ROLE_LABELS[adminProf().role] || 'Member'})
                        </summary>
                        <div class="v2-collapsible__body">
                          <form class="v2-user-detail__admin-form" onSubmit={handleProfileSave}>
                            <div class="v2-user-detail__admin-grid">
                              <label class="v2-user-detail__admin-field">
                                <span>Access Group</span>
                                <select
                                  name="role"
                                  class="v2-input"
                                  value={adminProf().role || 'member'}
                                  disabled={adminProf().unlinked || adminProf().role === 'owner' || (!isOwner() && ['owner', 'family', 'staff'].includes(adminProf().role))}
                                >
                                  <For each={Object.entries(ROLE_LABELS)}>{([val, lbl]) => (
                                    <option
                                      value={val}
                                      disabled={!isOwner() && ['owner', 'family', 'staff'].includes(val) && val !== adminProf().role}
                                    >
                                      {lbl}
                                    </option>
                                  )}</For>
                                </select>
                              </label>

                              <label class="v2-user-detail__admin-check" title={!isOwner() ? 'Only Echo (Owner) can grant or revoke Family tier access' : undefined}>
                                <input
                                  name="familyMember"
                                  type="checkbox"
                                  checked={!!adminProf().familyMember}
                                  disabled={adminProf().unlinked || adminProf().role === 'owner' || !isOwner()}
                                />
                                <span>🏠 Family tier access</span>
                              </label>

                              <label class="v2-user-detail__admin-check">
                                <input
                                  name="relayContributor"
                                  type="checkbox"
                                  checked={!!adminProf().relayContributor}
                                  disabled={adminProf().unlinked}
                                />
                                <span>📡 Relay contributor authorized</span>
                              </label>
                            </div>

                            <label class="v2-user-detail__admin-field" style={{ 'margin-top': '8px' }}>
                              <span>Staff / Governance Note</span>
                              <textarea
                                name="notes"
                                class="v2-input"
                                rows="2"
                                maxlength="2000"
                                value={adminProf().notes || ''}
                                placeholder={adminProf().unlinked ? 'Link this ESO account to a Discord profile first to edit permissions' : 'Optional staff note…'}
                                disabled={adminProf().unlinked}
                              />
                            </label>

                            <div class="v2-toolbar" style={{ 'margin-top': '8px', 'justify-content': 'flex-end' }}>
                              <Show when={user().discordId && !alreadySpying()}>
                                <button
                                  type="button"
                                  class="v2-btn-sm"
                                  onClick={() => spyAs(user().discordId)}
                                >
                                  {EMOJI.spy} Spy as {user().tag}
                                </button>
                              </Show>
                              <button
                                type="submit"
                                class="v2-btn-sm is-active"
                                disabled={adminProf().unlinked || savingProfile()}
                              >
                                {adminProf().unlinked
                                  ? 'Discord Link Required'
                                  : savingProfile()
                                  ? 'Saving…'
                                  : '💾 Save Profile Permissions'}
                              </button>
                            </div>
                          </form>
                        </div>
                      </details>
                    </Show>
                  </div>
                </Match>
              </Switch>
            </div>
          </div>
        </Show>
      </Show>
    </Show>
  );
}
